import { ENDPOINT_PATHS } from './endpoint-paths';

/**
 * Endpoint path -> the permission NAME a session must hold to use it.
 *
 * Ported from the legacy `src/core/data/permissions.data.ts`. This is the join between the two
 * key spaces the model uses, and confusing them is the easiest way to ship a broken gate:
 *
 *   endpoint path        `/admin/customer-management/customers/get`   what a nav entry declares
 *   permission name      `can_get_customers`                          what the session's token carries
 *
 * A nav entry declares paths; the session carries names; this map is what turns one into the
 * other. `PermissionWrapper` and `usePermission` take NAMES directly, because that is what the
 * legacy call sites pass.
 *
 * 268 of the 272 known paths are mapped. An unmapped path grants nothing and
 * is treated as denied — see `permissionForPath`.
 */
export const ENDPOINT_PERMISSIONS: Partial<Record<string, string>> = {
  [ENDPOINT_PATHS.AdminApiKeyPost]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminApiKeyPut]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminApiQuotaPost]: 'can_update_api_quota',
  [ENDPOINT_PATHS.AdminApiKeyManagerListPost]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminBillingAdjustmentGet]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminBillingAdjustmentPost]: 'billing_admin',
  [ENDPOINT_PATHS.AdminBillingAdjustmentDelete]: 'super_admin',
  [ENDPOINT_PATHS.AdminBillingAdjustmentPaymentPost]: 'billing_admin',
  [ENDPOINT_PATHS.AdminBillingAdjustmentUsersGet]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminClientCredentialsPost]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminCustomerManagementCustomersGet]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminCustomerManagementMlsCustomerGet]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminDiagnosticGet]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminEntitiesBttPermissionAdvantagesPost]: 'can_get_permissions',
  [ENDPOINT_PATHS.AdminEntitiesBttTypeItemsPost]: 'can_get_btts',
  [ENDPOINT_PATHS.AdminEntitiesFeedTypesConnectionTypesPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminEntitiesGroupsPost]: 'can_get_groups',
  [ENDPOINT_PATHS.AdminEntitiesInvoiceItemPackagesPost]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminEntitiesInvoicesPost]: 'can_get_customers',
  [ENDPOINT_PATHS.AdminEntitiesLanguagesPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminEntitiesLocationsPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminEntitiesPackageDescriptionsPost]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminEntitiesPackageBttItemsPost]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminEntitiesPackagesPreReqBttItemsPost]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminEntitiesPackagesPost]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminEntitiesPaymentMethodPost]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminEntitiesPromotionsPost]: 'can_get_promotions',
  [ENDPOINT_PATHS.AdminEntitiesRolesPost]: 'can_get_roles',
  [ENDPOINT_PATHS.AdminEntitiesStaffsPost]: 'can_get_staff',
  [ENDPOINT_PATHS.AdminEntitiesUsagePlansPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminEntitiesUserProfessionsPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminEntitiesUsersPost]: 'can_get_users',
  [ENDPOINT_PATHS.AdminPaymentPlanGpmsMAll]: 'super_admin',
  [ENDPOINT_PATHS.AdminPaymentPlanGpmsGet]: 'can_get_groups',
  [ENDPOINT_PATHS.AdminPaymentPlanGpmsPost]: 'group_billing_admin',
  [ENDPOINT_PATHS.AdminPaymentPlanGpmsDelete]: 'group_billing_admin',
  [ENDPOINT_PATHS.AdminPaymentPlanGpmsPut]: 'group_billing_admin',
  [ENDPOINT_PATHS.AdminRotateClientIdPost]: 'can_modify_api_keys',
  [ENDPOINT_PATHS.AdminSystemCountryListGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AdminUsagePlansMAll]: 'super_admin',
  [ENDPOINT_PATHS.AdminUsagePlansGet]: 'can_get_packages',
  [ENDPOINT_PATHS.AdminUsagePlansPost]: 'can_manage_packages',
  [ENDPOINT_PATHS.AdminUsagePlansDelete]: 'can_manage_packages',
  [ENDPOINT_PATHS.AdminUsagePlansPut]: 'can_manage_packages',
  [ENDPOINT_PATHS.BenTestPost]: 'can_manage_staff_role',
  [ENDPOINT_PATHS.BttAddEditPost]: 'can_manage_btts',
  [ENDPOINT_PATHS.BttDeletePost]: 'can_delete_btts',
  [ENDPOINT_PATHS.GroupBttPAllMAll]: 'group_admin',
  [ENDPOINT_PATHS.GroupBttBttListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupBttGroupBttListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupBttManageBttPost]: 'group_admin',
  [ENDPOINT_PATHS.GroupBttManageBttGet]: 'can_get_groups',
  [ENDPOINT_PATHS.AdminPackagesBttItemsV2Get]: 'can_manage_packages',
  [ENDPOINT_PATHS.AdminPackagesPreReqBttItemsV2Get]: 'can_manage_packages',
  [ENDPOINT_PATHS.GroupJoinApprovementPAllMAll]: 'group_admin',
  [ENDPOINT_PATHS.GroupJoinApprovementJoinApprovementPost]: 'group_admin',
  [ENDPOINT_PATHS.GroupJoinApprovementJoinListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupManagePAllMAll]: 'manage_group',
  [ENDPOINT_PATHS.GroupManageAddDocumentPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageAddLinkPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageBasicInfoPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageCreatePost]: 'group_admin',
  [ENDPOINT_PATHS.GroupManageExpertiseAreaActivityPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageGroupTypePatch]: 'group_admin',
  [ENDPOINT_PATHS.GroupManageItemGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupManageListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupManageModifyDocumentPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageModifyLinkPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageRemoveDocumentPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageRemoveLinkPatch]: 'group_management',
  [ENDPOINT_PATHS.GroupManageUniqueFieldGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupMemberPAllMAll]: 'manage_group',
  [ENDPOINT_PATHS.GroupMemberListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupMemberRolePatch]: 'group_admin',
  [ENDPOINT_PATHS.GroupWhiteListPAllMAll]: 'manage_group',
  [ENDPOINT_PATHS.GroupWhiteListListGet]: 'can_get_groups',
  [ENDPOINT_PATHS.GroupWhiteListListPost]: 'group_management',
  [ENDPOINT_PATHS.GroupWhiteListRemoveDelete]: 'group_management',
  [ENDPOINT_PATHS.JaydenFirstPost]: 'super_admin',
  [ENDPOINT_PATHS.AdminClientCacheManageGetIpList]: 'super_admin',
  [ENDPOINT_PATHS.MembershipAdminDiscountsPost]: 'can_manage_discount',
  [ENDPOINT_PATHS.MembershipAdminDiscountsDelete]: 'can_manage_discount',
  [ENDPOINT_PATHS.MembershipAdminDiscountsPut]: 'can_manage_discount',
  [ENDPOINT_PATHS.MembershipAdminDiscountsGet]: 'can_get_discounts',
  [ENDPOINT_PATHS.MembershipAdminPackagesV2MAll]: 'super_admin',
  [ENDPOINT_PATHS.MembershipAdminPackagesV2Get]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesV2Post]: 'can_manage_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesV2Put]: 'can_manage_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesV2Delete]: 'can_manage_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesDelete]: 'super_admin',
  [ENDPOINT_PATHS.MembershipAdminPackagesPost]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesPut]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPackagesGet]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPaymentMethodsPost]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPaymentMethodsPut]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipAdminPromossionsPost]: 'can_manage_promotion',
  [ENDPOINT_PATHS.MembershipAdminPromossionsPut]: 'can_manage_promotion',
  [ENDPOINT_PATHS.MembershipAdminPromossionsDelete]: 'can_manage_promotion',
  [ENDPOINT_PATHS.MembershipMlsServersGet]: 'super_admin',
  [ENDPOINT_PATHS.MembershipPaymentPlanMlsDataMAll]: 'super_admin',
  [ENDPOINT_PATHS.MembershipPaymentPlanMlsDataGet]: 'can_get_packages',
  [ENDPOINT_PATHS.MembershipPaymentPlanMlsDataPost]: 'can_manage_packages',
  [ENDPOINT_PATHS.MembershipPaymentPlanMlsDataPut]: 'can_manage_packages',
  [ENDPOINT_PATHS.MembershipUserActiveServicesGet]: 'can_get_customers',
  [ENDPOINT_PATHS.MultiLanguageDictionariesPut]: 'can_edit_language_files',
  [ENDPOINT_PATHS.MultiLanguageDictionariesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.AgreementTemplateGet]: 'can_edit_unpublished_agreements',
  [ENDPOINT_PATHS.AgreementSignedGet]: 'can_edit_unpublished_agreements',
  [ENDPOINT_PATHS.DealCompletedAdminGet]: 'super_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesDelete]: 'super_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesPAllPut]: 'language_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesAll]: 'language_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesAddPost]: 'language_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesDeleteDraftDelete]: 'language_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesDeletePublishedDelete]: 'super_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesEditPut]: 'language_admin',
  [ENDPOINT_PATHS.MultiLanguageDictionariesPublishPut]: 'language_admin',
  [ENDPOINT_PATHS.NegotiatorApiV1PAllMAll]: 'super_admin',
  [ENDPOINT_PATHS.NotificationsAdminTemplatePost]: 'notification_admin',
  [ENDPOINT_PATHS.NotificationsActionsMAll]: 'super_admin',
  [ENDPOINT_PATHS.NotificationsActionsGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsActionsPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsActionsPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsActionsBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsActionsPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsActionsPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsActionsPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsApplicationPost]: 'super_admin',
  [ENDPOINT_PATHS.NotificationsApplicationPAllMAll]: 'super_admin',
  [ENDPOINT_PATHS.NotificationsCategoriesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsCategoriesPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCategoriesBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsCategoriesPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCategoriesPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCategoriesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsCategoriesPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsMessagesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsMessagesPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsMessagesBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMessagesPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsMessagesPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsMessagesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMessagesPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsNestedGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsNestedPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsSettingsPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsSettingsPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsSettingsPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsSettingsGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsSettingsPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsSettingsPAllMAll]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsSettingsBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsSettingsListGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsSettingsListPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsTemplateGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsTemplateDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsTemplatePut]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsTemplatePAllMAll]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsTemplateBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsTokenMAll]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsVariablesPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsVariablesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsVariablesPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsVariablesBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsVariablesPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsVariablesPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsVariablesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsCodesPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCodesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsCodesBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsCodesPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCodesPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCodesPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsCodesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesPAllGet]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsMicroservicesBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesPAllPut]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesPAllPatch]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsMicroservicesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsPAllGet]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsApplicationsPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsBulkPost]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsPAllPut]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsPAllPatch]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesPAllGet]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesPost]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesPAllPut]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesPAllDelete]: 'notif_admin',
  [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesPAllPatch]: 'notif_manager',
  [ENDPOINT_PATHS.NotificationsVariablesPAllMAll]: 'notif_admin',
  [ENDPOINT_PATHS.PermissionAddPost]: 'super_admin',
  [ENDPOINT_PATHS.PermissionAssignPost]: 'super_admin',
  [ENDPOINT_PATHS.PermissionDeletePost]: 'super_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestPut]:
    'can_manage_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestGet]:
    'can_get_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestDocumentMAll]:
    'can_manage_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestDocumentGet]:
    'can_get_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestDocumentPost]:
    'can_manage_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestDocumentPut]:
    'can_manage_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestDocumentDelete]:
    'can_manage_mls_access_customer_request',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPost]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPut]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessCredentialGet]: 'can_get_mls_credential',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessCredentialPost]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessCredentialPut]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessCredentialDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodPost]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodPut]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodDelete]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodFlatRateItemPut]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodFlatRateItemGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodFlatRateItemDelete]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodFlatRateItemPost]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodRangeRateItemPut]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodRangeRateItemGet]:
    'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodRangeRateItemDelete]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsAccessPaymenMethodRangeRateItemPost]:
    'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsConfigPost]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsConfigPut]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsConfigGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsConfigDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsDocumentPost]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsDocumentPut]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsDocumentGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsDocumentDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerPost]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerPut]: 'can_manage_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsMlsAccessCredentialPut]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsMlsAccessCredentialPost]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsMlsAccessCredentialGet]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsMlsAccessCredentialDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformPut]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformPost]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformGet]: 'can_get_mls_server',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformCredentialPut]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformCredentialPost]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformCredentialGet]: 'mls_server_admin',
  [ENDPOINT_PATHS.ProvidersPlatformsPlatformCredentialDelete]: 'mls_server_admin',
  [ENDPOINT_PATHS.RfBackendAddStaffPost]: 'can_manage_staff',
  [ENDPOINT_PATHS.RfBackendAssignRolePost]: 'can_manage_staff_role',
  [ENDPOINT_PATHS.RfBackendProfileDetailsGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.RfBackendProfileSetupPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.UsagePlansListGet]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.UserActivatePost]: 'can_manage_users',
  [ENDPOINT_PATHS.UserdeactivatePost]: 'can_manage_users',
  [ENDPOINT_PATHS.RfBackendSignUpPost]: '*',
  [ENDPOINT_PATHS.RfBackendAuthorizationPost]: '*',
  [ENDPOINT_PATHS.UploaderPost]: 'Staff_RFBack_Login',
  [ENDPOINT_PATHS.MembershipAdminPaymentMethodsV2Post]: 'can_manage_packages',
  [ENDPOINT_PATHS.ProfileSetupProfessionsListGet]: 'basic_user',
  [ENDPOINT_PATHS.ProfileSetupProfileDetailsGet]: 'basic_user',
  [ENDPOINT_PATHS.SuggestionHashtagNormalPost]: 'basic_user',
  [ENDPOINT_PATHS.LocationSuggestGet]: 'basic_user',
  [ENDPOINT_PATHS.LocationGetGet]: 'basic_user',
  [ENDPOINT_PATHS.LocationZipcodePost]: 'basic_user',
  [ENDPOINT_PATHS.ResourcesGet]: 'can_get_packages',
};

/**
 * Every distinct permission name the map refers to.
 *
 * Useful for asserting that a name a component gates on is one the backend actually issues —
 * a typo'd name silently hides a control from everyone, which is the failure mode this model is
 * most prone to.
 */
export const KNOWN_PERMISSION_NAMES = [
  '*',
  'Staff_RFBack_Login',
  'basic_user',
  'billing_admin',
  'can_delete_btts',
  'can_edit_language_files',
  'can_edit_unpublished_agreements',
  'can_get_btts',
  'can_get_customers',
  'can_get_discounts',
  'can_get_groups',
  'can_get_mls_access_customer_request',
  'can_get_mls_credential',
  'can_get_mls_server',
  'can_get_packages',
  'can_get_permissions',
  'can_get_promotions',
  'can_get_roles',
  'can_get_staff',
  'can_get_users',
  'can_manage_btts',
  'can_manage_discount',
  'can_manage_mls_access_customer_request',
  'can_manage_mls_server',
  'can_manage_packages',
  'can_manage_promotion',
  'can_manage_staff',
  'can_manage_staff_role',
  'can_manage_users',
  'can_modify_api_keys',
  'can_update_api_quota',
  'group_admin',
  'group_billing_admin',
  'group_management',
  'language_admin',
  'manage_group',
  'mls_server_admin',
  'notif_admin',
  'notif_manager',
  'notification_admin',
  'super_admin',
] as const;

export type PermissionName = (typeof KNOWN_PERMISSION_NAMES)[number];
